import { defineStore } from "pinia";
import { Login, Logout } from "~/api/auth/login";
import { fetchWrapper } from "~/api/fetchWrapper";
import { setBackendLanguage } from "~/api/i18n";

import type {
  CompletaRegistrazioneEntitaRequestBody,
  CompletaRegistrazioneRequestBody,
  LoginRequestBody,
} from "~/interfaces/auth";
import {
  KobakContext,
  type User,
  type UserInfo,
  type UserType,
  hasPermission,
} from "~/interfaces/user";
import { useUserStore } from "./user";

export const TOKEN_REFRESH_PATH = "/auth/token/refresh/";
export const LOGIN_PAGE = "/login";

// Single-flight promise for refreshToken to avoid concurrent refresh requests
let refreshPromise: Promise<boolean> | null = null;
// Flag to block further refresh attempts after first failure (reset on login)
let refreshFailed = false;

interface AuthState {
  _user: User | null;
  accessToken: string | null;
  context: KobakContext;
}

const INITIAL_STATE = {
  _user: null,
  accessToken: null,
  context: KobakContext.operatore,
};

const contextKeys = {
  operatore: "operatore",
  concessionario: "concessionario",
  capoArea: "capo_area",
};

export const useAuthStore = defineStore("auth", {
  persist: {
    storage: localStorage,
  },
  state: (): AuthState => INITIAL_STATE,

  getters: {
    isAuthenticated: (state) => state._user && state.accessToken !== null,
    user: (state) => state._user,

    userType: (state): UserType | undefined => {
      const userType = state._user?.user_type;
      if (!userType) return undefined;

      if (typeof userType === "string") {
        return userType as UserType;
      } else if (typeof userType === "object" && "value" in userType) {
        return userType.value;
      }

      return undefined;
    },

    currentRole: (state) => {
      return contextKeys[state.context];
    },

    hasPermission: (state) => {
      return (allowedUserTypes: UserType[]): boolean => {
        const currentUserType = state._user?.user_type;
        return hasPermission(currentUserType, allowedUserTypes);
      };
    },

    userCountry: (state) => state._user?.country,
    canAccessCountry: (state) => {
      return (targetCountry: string): boolean => {
        if (!state._user?.country) return true;
        return state._user.country === targetCountry;
      };
    },
  },

  actions: {
    clearToLoginPage() {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      this.$reset();
      this.accessToken = null;
      this._user = null;

      const userStore = useUserStore();
      userStore.clearUser();

      const route = useRoute();
      const localePath = useLocalePath();
      if (!route.path.includes(LOGIN_PAGE)) {
        return navigateTo(localePath(LOGIN_PAGE));
      }
    },

    async login(
      credentials: LoginRequestBody,
      options?: {
        showToast?: boolean;
        toastMessage?: string;
        toastErrorMessage?: string;
        toastParams?: Record<string, unknown>;
      },
    ) {
      refreshFailed = false;
      const response = await Login(
        credentials,
        (key, params) => this.$nuxt.$t(key, params),
        options,
      );
      this.accessToken = response.access;
      this._user = response.user;

      const userStore = useUserStore();
      userStore.user = response.user;

      try {
        await userStore.fetchUser({ showToast: false });
        this._user = userStore.user;
      } catch (error) {
        console.warn("Login: impossibile aggiornare l'utente con /me", error);
      }

      try {
        const { $i18n } = useNuxtApp();
        await setBackendLanguage($i18n.locale.value);
      } catch {
        // Best-effort: non blocca il login
      }

      try {
        const { $i18n } = useNuxtApp();
        if (this._user?.lang && this._user.lang !== $i18n.locale.value) {
          $i18n.setLocale(this._user.lang);
        }
      } catch {
        // Best-effort: non blocca il login
      }

      return this._user;
    },

    async loadUserInfo(options?: {
      showToast?: boolean;
      toastMessage?: string;
      toastParams?: Record<string, unknown>;
    }) {
      try {
        const response = await fetchWrapper.get(
          "users/me/",
          undefined,
          undefined,
          (key, params) => this.$nuxt.$t(key, params),
          options,
        );
        const userInfo = response?._data as UserInfo;

        this._user = {
          id: userInfo.id,
          nome: userInfo.nome,
          foto: userInfo.foto,
          email: userInfo.email,
          url: userInfo.url,
          lang: userInfo.lang,
          user_type: userInfo.user_type,
          country: userInfo.country,
        };

        const userStore = useUserStore();
        userStore.user = this._user;

        return true;
      } catch (e) {
        console.error("loadUserInfo error:", e);
        return false;
      }
    },

    async completaRegistrazione(
      body: CompletaRegistrazioneRequestBody,
      options?: {
        showToast?: boolean;
        toastMessage?: string;
        toastErrorMessage?: string;
        toastParams?: Record<string, unknown>;
      },
    ) {
      try {
        const response = await fetchWrapper.post(
          "auth/completa_registrazione/",
          body,
          undefined,
          (key, params) => this.$nuxt.$t(key, params),
          options,
        );
        return response?._data;
      } catch (error) {
        console.error("completaRegistrazione error:", error);
        throw error;
      }
    },

    async completaRegistrazioneEntita(
      body: CompletaRegistrazioneEntitaRequestBody,
      options?: {
        showToast?: boolean;
        toastMessage?: string;
        toastErrorMessage?: string;
        toastParams?: Record<string, unknown>;
      },
    ) {
      try {
        const response = await fetchWrapper.post(
          "auth/completa_registrazione_entita/",
          body,
          undefined,
          (key, params) => this.$nuxt.$t(key, params),
          options,
        );
        return response?._data;
      } catch (error) {
        console.error("completaRegistrazioneEntita error:", error);
        throw error;
      }
    },

    async logout(options?: {
      showToast?: boolean;
      toastMessage?: string;
      toastParams?: Record<string, unknown>;
    }) {
      try {
        await Logout((key, params) => this.$nuxt.$t(key, params), options);
        this.clearToLoginPage();
        return true;
      } catch (e) {
        console.error("logout error:", e);
        return false;
      }
    },

    async refreshToken(_options?: {
      showToast?: boolean;
      toastMessage?: string;
      toastParams?: Record<string, unknown>;
    }) {
      if (refreshFailed) return false;
      if (refreshPromise) return await refreshPromise;

      refreshPromise = (async () => {
        try {
          const apiUrl = useRuntimeConfig().public.apiUrl;
          const raw = await $fetch.raw(`${apiUrl}${TOKEN_REFRESH_PATH}`, {
            method: "POST",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          });
          const data = (raw as any)?._data;

          if (data?.access) {
            this.accessToken = data.access;
            return true;
          }
          return false;
        } catch (e) {
          console.error("refreshToken error:", e);
          refreshFailed = true;
          this._user = null;
          this.accessToken = null;
          this.clearToLoginPage();
          return false;
        } finally {
          refreshPromise = null;
        }
      })();

      return await refreshPromise;
    },
  },
});
